import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { getImageFileFromClipboardEvent } from '../utils/clipboardImage';
import {
  documentUpload,
  isDocumentWithinSizeLimit,
} from '../utils/userFacingCopy';

export interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[], description: string) => Promise<void>;
  uploading: boolean;
  title?: string;
  allowMultiple?: boolean;
}

export function DocumentUploadDialog({
  open,
  onClose,
  onUpload,
  uploading,
  title = 'Upload Supporting Document',
  allowMultiple = true,
}: DocumentUploadDialogProps) {
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadDescription, setUploadDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (!open) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      const id = setTimeout(() => {
        setUploadFiles([]);
        setUploadDescription('');
        setUploadError('');
      }, 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  const handleClose = () => {
    setUploadFiles([]);
    setUploadDescription('');
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const validateFiles = (files: File[]): string | null => {
    for (const file of files) {
      if (!isDocumentWithinSizeLimit(file)) {
        return documentUpload.tooLargeMessage;
      }
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const nextFiles = allowMultiple ? Array.from(files) : files[0] ? [files[0]] : [];
    const validationError = validateFiles(nextFiles);
    if (validationError) {
      setUploadError(validationError);
      e.target.value = '';
      return;
    }
    setUploadError('');
    if (allowMultiple) {
      setUploadFiles((prev) => [...prev, ...nextFiles]);
    } else {
      setUploadFiles(nextFiles);
    }
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (uploadFiles.length === 0) return;
    await onUpload(uploadFiles, uploadDescription);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const file = getImageFileFromClipboardEvent(e);
    if (file) {
      e.preventDefault();
      const validationError = validateFiles([file]);
      if (validationError) {
        setUploadError(validationError);
        return;
      }
      setUploadError('');
      if (allowMultiple) {
        setUploadFiles((prev) => [...prev, file]);
      } else {
        setUploadFiles([file]);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent onPaste={handlePaste}>
        {open ? (
          <>
            <Box sx={{ mb: 2 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept={documentUpload.accept}
                multiple={allowMultiple}
                onChange={handleFileChange}
                id="document-upload-input"
                style={{ display: 'none' }}
              />
              <label htmlFor="document-upload-input">
                <Button variant="outlined" component="span" fullWidth startIcon={<AttachFileIcon />} sx={{ textTransform: 'none', mb: 1 }}>
                  {uploadFiles.length === 0 ? 'Select File' : `Select ${allowMultiple ? 'More Files' : 'File'} (${uploadFiles.length} selected)`}
                </Button>
              </label>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: uploadFiles.length > 0 ? 1 : 0.5 }}>
                {documentUpload.helperText}
              </Typography>
              {uploadError ? (
                <Typography variant="caption" display="block" color="error" sx={{ mb: 1 }}>
                  {uploadError}
                </Typography>
              ) : null}
              {uploadFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {uploadFiles.map((f, i) => (
                    <Chip
                      key={`${f.name}-${i}`}
                      label={f.name}
                      size="small"
                      onDelete={() => handleRemoveFile(i)}
                      sx={{ maxWidth: 200 }}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="caption" display="block" color="text.secondary">
                You can also paste a screenshot (Ctrl+V).
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Description (optional)"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
            <DialogActions>
              <Button onClick={handleClose} sx={{ textTransform: 'none' }} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} variant="contained" disabled={uploading || uploadFiles.length === 0} sx={{ textTransform: 'none' }}>
                {uploading ? <CircularProgress size={24} /> : 'Upload'}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default DocumentUploadDialog;
